import { BaseConfig } from "../config.interface";
import { IconColor, PrimeColor } from "../util";
export type IconPosition = 'left' | 'right';

interface IButtonConfig {
    color?: PrimeColor;
    iconColor?: IconColor;
    style: 'default' | 'raised' | 'outlined' | 'text' | 'plain' | 'icon';
    rounded?: boolean;
    type?: 'auto' | 'wide' | 'full';
    iconPosition?: IconPosition;
}

export class ButtonConfig extends BaseConfig<IButtonConfig> implements IButtonConfig {
    /**
     * Color of the button or its outline
     */
    color?: PrimeColor;
    /**
     * Color of the icon. 
     */
    iconColor?: IconColor;
    /**
     * Style of the button.
     * @values "text" | "default" | "raised" | "outlined" | "plain" | "icon"
     */
    style: "text" | "default" | "raised" | "outlined" | "plain" | "icon";
    /**
     * Rounded corners
     */
    rounded?: boolean;
    /**
     * Auto sizing of its width or enforce `wide` (fixed pixel) size.
     */
    type?: "auto" | "wide" | "full";
    /**
     * Position of the icon 
     * @values "left" |"right"
     */
    iconPosition?: IconPosition;
    /**
     * @theme Base
     */
    static baseTheme = {
        // #region Types
        /**
         * Default configuration.
         * Style is "default" with color as "primary".
         */
        Default: new ButtonConfig({
            style:     'default',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'auto',
        }),
        /**
         * Default configuration with type full.
         * Style is "default" with color as "primary".
         * @remark Refrain from using type `full` if possible, as we want to avoid full width buttons.
         */
        Full: new ButtonConfig({
            style:     'default',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'full',
        }),
        /**
         * Raised button configuration.
         * Style is "raised" with color as "primary".
         */
        Raised: new ButtonConfig({
            style:     'raised',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'auto',
        }),
        /**
         * Outlined button configuration.
         * Style is "outlined" with color as "primary".
         */
        Outlined: new ButtonConfig({
            style:     'outlined',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'auto',
        }),
        /**
         * Outlined button configuration.
         * Style is "outlined" with color as "danger".
         */
        OutlinedDanger: new ButtonConfig({
            style:     'outlined',
            color:     'danger',
            iconColor: 'danger',
            rounded:   false,
            type:      'auto',
        }),
        /**
         * Outlined button configuration.
         * Style is "outlined" with color as "secondary".
         */
        OutlinedSecondary: new ButtonConfig({
            style:     'outlined',
            color:     'secondary',
            iconColor: 'secondary',
            rounded:   false,
            type:      'auto',
        }),
        OutlinedWide: new ButtonConfig({
            style:     'outlined',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'wide',
        }),

        OutlinedFull: new ButtonConfig({
            style:     'outlined',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'full',
        }),
        /**
         * Wide button configuration.
         * Style is "default" with color as "primary".
         */
        Wide: new ButtonConfig({
            style:     'default',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'wide',
        }),
        /**
         * Text button configuration.
         * Style is "text" with color as "secondary".
         */
        Text: new ButtonConfig({
            style:     'text',
            color:     'secondary',
            iconColor: 'primary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Icon button configuration.
         * Style is "icon" with color as "primary".
         */
        Icon: new ButtonConfig({
            style:     'icon',
            color:     'primary',
            iconColor: 'primary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Text link button configuration.
         * Style is "text" with color as "primary".
         */
        TextLink: new ButtonConfig({
            style:     'text',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'auto',
        }),
        // #endregion

        // #region Color-Styles
        /**
         * Primary color style configuration.
         * Style is "default" with color as "primary".
         */
        Primary: new ButtonConfig({
            style:     'default',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'auto',
        }),
        /**
         * Secondary color style configuration.
         * Style is "default" with color as "secondary".
         */
        Secondary: new ButtonConfig({
            style:     'default',
            color:     'secondary',
            iconColor: 'secondary',
            rounded:   false,
            type:      'auto',
        }),
        /**
         * Info color style configuration.
         * Style is "default" with color as "info".
         */
        Info: new ButtonConfig({
            style:     'default',
            color:     'info',
            iconColor: 'info',
            rounded:   false,
            type:      'auto',
        }),
        /**
         * Success color style configuration.
         * Style is "default" with color as "success".
         */
        Success: new ButtonConfig({
            style:     'default',
            color:     'success',
            iconColor: 'success',
            rounded:   false,
            type:      'auto',
        }),
        /**
         * Warning color style configuration.
         * Style is "default" with color as "warn".
         */
        Warning: new ButtonConfig({
            style:     'default',
            color:     'warn',
            iconColor: 'warn',
            rounded:   false,
            type:      'auto',
        }),
        /**
         * Danger color style configuration.
         * Style is "default" with color as "danger".
         */
        Danger: new ButtonConfig({
            style:     'default',
            color:     'danger',
            iconColor: 'danger',
            rounded:   false,
            type:      'auto',
        }),
        // #endregion


        SuccessFull: new ButtonConfig({
            style:     'default',
            color:     'success',
            iconColor: 'success',
            rounded:   false,
            type:      'full',
        }),
        DangerFull: new ButtonConfig({
            style:     'default',
            color:     'danger',
            iconColor: 'danger',
            rounded:   false,
            type:      'full',
        }),

        // #region Icon Color-Styles
        /**
         * Info icon style configuration.
         * Style is "icon" with color as "primary".
         */
        PrimaryIcon: new ButtonConfig({
            style:     'icon',
            color:     'primary',
            iconColor: 'primary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Info icon style configuration.
         * Style is "icon" with color as "info".
         */
        InfoIcon: new ButtonConfig({
            style:     'icon',
            color:     'info',
            iconColor: 'info',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Success icon style configuration.
         * Style is "icon" with color as "success".
         */
        SuccessIcon: new ButtonConfig({
            style:     'icon',
            color:     'success',
            iconColor: 'success',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Warning icon style configuration.
         * Style is "icon" with color as "warn".
         */
        WarningIcon: new ButtonConfig({
            style:     'icon',
            color:     'warn',
            iconColor: 'warn',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Danger icon style configuration.
         * Style is "icon" with color as "danger".
         */
        DangerIcon: new ButtonConfig({
            style:     'icon',
            color:     'danger',
            iconColor: 'danger',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Secondary icon button configuration.
         * Style is "icon" with color as "secondary".
         */
        SecondaryIcon: new ButtonConfig({
            style:     'icon',
            color:     'secondary',
            iconColor: 'secondary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Text icon button configuration.
         * Style is "icon" with color as "secondary".
         */
        TextIcon: new ButtonConfig({
            style:     'icon',
            color:     'secondary',
            iconColor: 'text',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Info icon rounded button configuration.
         * Style is "default" with color as "info".
         */
        InfoIconRounded: new ButtonConfig({
            style:     'default',
            color:     'info',
            iconColor: 'primary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Success icon rounded button configuration.
         * Style is "default" with color as "success".
         */
        SuccessIconRounded: new ButtonConfig({
            style:     'default',
            color:     'success',
            iconColor: 'primary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Warning icon rounded button configuration.
         * Style is "default" with color as "warn".
         */
        WarningIconRounded: new ButtonConfig({
            style:     'default',
            color:     'warn',
            iconColor: 'primary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Danger icon rounded button configuration.
         * Style is "default" with color as "danger".
         */
        DangerIconRounded: new ButtonConfig({
            style:     'default',
            color:     'danger',
            iconColor: 'primary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Secondary icon rounded button configuration.
         * Style is "default" with color as "secondary".
         */
        SecondaryIconRounded: new ButtonConfig({
            style:     'default',
            color:     'secondary',
            iconColor: 'primary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Primary icon rounded button configuration.
         * Style is "default" with color as "secondary".
         */
        PrimaryIconRounded: new ButtonConfig({
            style:     'default',
            color:     'secondary',
            iconColor: 'primary',
            rounded:   true,
            type:      'auto',
        }),
        /**
         * Text icon rounded button configuration.
         * Style is "default" with color as "secondary".
         * Icon color is text in both light & dark mode.
         */
        TextIconRounded: new ButtonConfig({
            style:     'outlined',
            color:     'secondary',
            iconColor: 'text',
            rounded:   true,
            type:      'auto',
        }),
        // #endregion
    };


    // #region Theme
    static hmm = {
        Default: new ButtonConfig({
            style:     'default',
            color:     'primary',
            iconColor: 'primary',
            rounded:   false,
            type:      'auto',
            variables: {
                '--v-border-radius': "var(--v-border-radius-none)"
            },
        }),
        Secondary: new ButtonConfig({
            style:     'outlined',
            color:     'secondary',
            iconColor: 'secondary',
            rounded:   false,
            type:      'auto',
            variables: {
                '--v-border-radius':     "var(--v-border-radius-none)",
                '--ion-color-secondary': "var(--gc-text-color)"
            },
        })
    };
    // #endregion
}